import type { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { changeColorAlpha } from '@elfsight/lib-utils';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../meta';

export type ItemAllowButtonsProps = PropsWithChildren<{
  onClick: (value: boolean) => void;
}>;

export const ItemAllowButtons = ({
  children,
  onClick
}: ItemAllowButtonsProps) => {
  const {
    yesButtonText,
    noButtonText,
    yesButtonColor,
    noButtonColor,
    textFont,
    shape
  } = useSettings();

  const clickHandler = (e, value) => {
    e.preventDefault();
    onClick(value);
  };

  return (
    <>
      <Container>
        <YesButton
          type="button"
          onClick={(e) => clickHandler(e, true)}
          _yesButtonColor={yesButtonColor}
          _fontSize={textFont.fontSize}
          _shape={shape}
        >
          {yesButtonText}
        </YesButton>

        <NoButton
          type="button"
          onClick={(e) => clickHandler(e, false)}
          _noButtonColor={noButtonColor}
          _fontSize={textFont.fontSize}
          _shape={shape}
        >
          {noButtonText}
        </NoButton>
      </Container>

      {children}
    </>
  );
};

const Container = styled.div.attrs({
  className: `${meta.prefix}-item-allow-buttons-container`
})`
  display: flex;
  margin: -10px;
`;

const Button = styled.button.attrs({
  className: `${meta.prefix}-item-allow-buttons-button`
})`
  position: relative;
  width: 50%;
  margin: 10px;
  padding: 12px;
  outline: none;
  background-color: transparent;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  color: rgb(255, 255, 255);
  border: none;
  top: 0;
  transition: 0.3s ease top;
  border-radius: ${({ _shape }) => (_shape ? '5px' : '0')};

  &:hover {
    top: -2px;
  }
`;

const YesButton = styled(Button).attrs({
  className: `${meta.prefix}-item-allow-buttons-yes`
})`
  display: block;
  background-color: ${({ _yesButtonColor }) => _yesButtonColor};
  color: ${({ _yesButtonColor }) =>
    getBrightInvertedColor(_yesButtonColor, [
      'rgb(255, 255, 255)',
      'rgb(29, 33, 41)'
    ])};
  font-size: ${({ _fontSize }) => _fontSize}px;
`;

const NoButton = styled(Button).attrs({
  className: `${meta.prefix}-item-allow-buttons-no`
})`
  display: block;
  border: 1px solid
    ${({ _noButtonColor }) => changeColorAlpha(_noButtonColor, -0.6)};
  color: ${({ _noButtonColor }) => _noButtonColor};
  font-size: ${({ _fontSize }) => _fontSize}px;
`;
